import { EXPIRATION_PERIOD_OPTIONS, FORM_AUTOMATION_TYPES, FREQUENCY_OPTIONS, SERVICE_SELECTION, TRIGGER_TIMING_OPTIONS } from 'settings/consultation-forms/consultationForms.const';

/**
 * @typedef {Object} Question
 * @property {string} id
 * @property {string} text
 * @property {boolean} required
 */

/**
 * @typedef {Object} Form
 * @property {string} name
 * @property {Question[]} questions
 */

/**
 * @typedef {Object} Rules
 * @property {string} trigger_type
 * @property {string} trigger_timing
 * @property {string} service_selection_type
 * @property {string[]} service_ids
 * @property {string} frequency
 * @property {string} expiration_period
 */

/**
 * @typedef {Object} ReviewRow
 * @property {string} key
 * @property {string} label
 * @property {string} value
 * @property {boolean} showServicesToggle
 */

/**
 * Resolves the review rows for a consultation form.
 *
 * @param {Form} form
 * @param {Rules} rules
 * @param {(key: string) => string} t
 * @returns {ReviewRow[]}
 */
export const resolveReviewRows = ({ form, rules, t }) => {
  const rows = [];

  rows.push({
    key: 'formName',
    label: t('consultationForms.review.formNameLabel'),
    value: form?.name ?? '',
  });

  const total = form?.questions?.length ?? 0;
  const required = (form?.questions ?? []).filter((q) => q.required === true).length;
  rows.push({
    key: 'questions',
    label: t('consultationForms.review.questionsLabel'),
    value: t('consultationForms.review.questionsValue', { total, required }),
  });

  const isManual = rules.trigger_type === FORM_AUTOMATION_TYPES.MANUAL;

  if (!isManual) {
    const timingOption = (TRIGGER_TIMING_OPTIONS[rules.trigger_type] ?? []).find((o) => o.value === rules.trigger_timing);
    rows.push({
      key: 'sendsWhen',
      label: t('consultationForms.review.sendsWhenLabel'),
      value: timingOption ? t(timingOption.labelKey) : '',
    });

    const isSelected = rules.service_selection_type === SERVICE_SELECTION.SELECTED;
    rows.push({
      key: 'services',
      label: t('consultationForms.review.servicesLabel'),
      value: isSelected ? t('consultationForms.review.servicesSelectedCount', { count: (rules.service_ids ?? []).length }) : t('consultationForms.review.servicesAll'),
      showServicesToggle: isSelected,
    });
  }

  const frequencyOption = FREQUENCY_OPTIONS.find((o) => o.value === rules.frequency);
  rows.push({
    key: 'frequency',
    label: t('consultationForms.review.frequencyLabel'),
    value: isManual ? t('consultationForms.review.frequencyManual') : frequencyOption ? t(frequencyOption.labelKey) : '',
  });

  const expiryOption = EXPIRATION_PERIOD_OPTIONS.find((o) => o.value === rules.expiration_period);
  rows.push({
    key: 'expiresAfter',
    label: t('consultationForms.review.expiresAfterLabel'),
    value: expiryOption ? t(expiryOption.labelKey) : '',
  });

  rows.push({
    key: 'sentVia',
    label: t('consultationForms.review.sentViaLabel'),
    value: t('consultationForms.review.sentViaValue'),
  });

  return rows;
};
